import { pdfjs } from "react-pdf";
import { Box, Typography, IconButton, Paper, CircularProgress } from "@mui/material";
import { Document, Page } from "react-pdf";
import VirtualizedTree from "./VirtualizedTree";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import PanToolOutlinedIcon from "@mui/icons-material/PanToolOutlined";
import FullScreenToggleButton from "./FullScreenToggleButton";


pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

/**
 * ReportsPageContent
 * Displays the reports view consisting of two columns:
 * - Left: a tree view of files and folders
 * - Right: a PDF preview of the selected file, with zoom/pan and page controls
 */

export default function ReportsPageContent({
  calculatedHeight,
  treeWidth,
  selectedFile,
  handleSelectFile,
  containerRef,
  pdfContainerRef,
  pageRef,
  dragPos,
  startToolbarDrag,
  goToPrevPage,
  goToNextPage,
  pageNumber,
  numPages,
  zoomOut,
  zoomIn,
  isPanning,
  isDocumentLoading,
  setIsDocumentLoading,
  setIsPanning,
  setPdfError,
  scale,
  onDocumentLoadSuccess,
  doDrag,
  endDrag,
  setIsResizingColumn,
  setResizeOffset,
  pdfError,
  pageSize,
  fullScreen,
  toggleFullScreen,
}) {

  if (selectedFile) {
    console.log("rendering Document with:", selectedFile);
  }

  return (
    <Box sx={{ height: calculatedHeight, display: "flex", position: "relative" }} onMouseMove={doDrag} onMouseUp={endDrag}>
      <Box sx={{ width: treeWidth, overflow: "auto" }}>
        <VirtualizedTree onSelectFile={handleSelectFile} containerHeight={calculatedHeight} />
      </Box>

      <Box
        onMouseDown={(e) => {
          setIsResizingColumn(true);
          setResizeOffset(e.clientX - treeWidth);
        }}
        sx={{
          width: "16px",
          cursor: "col-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          backgroundColor: "transparent",
          zIndex: 10,
          "&:hover": {
            "&::before": {
              backgroundColor: "#64b5f6",
              width: "3px",
              boxShadow: "0 0 4px rgba(0, 0, 0, 0.2)",
            },
          },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            bottom: 0,
            left: "50%",
            width: "2px",
            backgroundColor: "#bdbdbd",
            transform: "translateX(-50%)",
            transition: "background-color 0.2s ease, width 0.2s ease, box-shadow 0.2s ease",
          },
        }}
      >
        <Paper
          elevation={1}
          sx={{
            width: "8px",
            height: "36px",
            borderRadius: "999px",
            border: "2px solid #bdbdbd",
            backgroundColor: "#ffffff",
            transition: "all 0.2s ease",
            zIndex: 2,
            "&:hover": {
              width: "10px",
              height: "40px",
              backgroundColor: "#90caf9",
            },
          }}
        />
      </Box>

      <Box ref={containerRef} sx={{ minHeight: 300, flex: 1, position: "relative", overflow: "hidden", backgroundColor: "#f5f5f5" }}>
        {selectedFile ? (
          <Box ref={pdfContainerRef} sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                top: dragPos.y,
                left: dragPos.x,
                transform: "translate(-50%, 0)",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                px: 1,
                py: 0.3,
                borderRadius: 2,
                zIndex: 1200,
                backgroundColor: "white",
                boxShadow: 3,
                cursor: "move",
                opacity: 0.7,
                "&:hover": { opacity: 1 },
              }}
              onMouseDown={startToolbarDrag}
            >
              <DragHandleIcon fontSize="small" sx={{ color: "grey.500", mr: 0.5, transform: "rotate(90deg)" }} />
              <IconButton size="small" onClick={goToPrevPage} disabled={pageNumber <= 1}>
                <ArrowBackIosIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption">{pageNumber} / {numPages || 0}</Typography>
              <IconButton size="small" onClick={goToNextPage} disabled={pageNumber >= numPages}>
                <ArrowForwardIosIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={zoomOut}><ZoomOutIcon fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => setIsPanning(!isPanning)}>
                <PanToolOutlinedIcon color={isPanning ? "primary" : "inherit"} fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={zoomIn}><ZoomInIcon fontSize="small" /></IconButton>
              <FullScreenToggleButton fullScreen={fullScreen} toggleFullScreen={toggleFullScreen} />

            </Paper>

            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                px: 2,
                pb: 2,
                cursor: isPanning ? "grab" : "default",
                userSelect: "none",
              }}
            >
              <Box sx={{
                border: "1px solid #ddd",
                borderRadius: "4px",
                boxShadow: "0 0 4px rgba(0,0,0,0.1)",
                backgroundColor: "white",
                minHeight: 200,
                minWidth: 200,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <Document
                  file={selectedFile}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(err) => {
                    console.error("PDF error:", err);
                    setIsDocumentLoading(false);
                    setPdfError("Could not load PDF: " + (err.message || "Unknown error"));
                  }}
                  loading={
                    <Box
                      sx={{
                        width: `${210 * scale}px`,   // A4 width in mm * scale
                        height: `${297 * scale}px`,  // A4 height in mm * scale
                        background: 'none',
                        border: 'none',
                        //backgroundColor: "white",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: 1,
                        boxShadow: "0 0 4px rgba(0,0,0,0.1)"
                      }}
                    >
                      <CircularProgress size={40} thickness={4} sx={{
                        background: 'none', border: 'none', boxShadow: 'none',
                      }} />
                    </Box>
                  }
                >
                  {numPages > 0 && Array.from(new Array(numPages), (_, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      scale={scale}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      inputRef={index === pageNumber - 1 ? pageRef : undefined}
                      loading={
                        <Box
                          sx={{
                            width: `${pageSize.width * scale}px`,
                            height: `${pageSize.height * scale}px`,
                            backgroundColor: "white",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            borderRadius: 1,
                            boxShadow: "0 0 4px rgba(0,0,0,0.1)",
                          }}
                        >
                          <CircularProgress size={40} sx={{ background: 'none', border: 'none', boxShadow: 'none', }} />
                        </Box>
                      }
                    />
                  ))}
                </Document>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: "center", mt: 10 }}>
            <Typography variant="h6">Ingen fil har valts – välj en för att visa innehållet.</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
